import api from '@/apis'
import { createLatestOnly } from '@/utils/latestOnly'
import { createUiSession } from '@/utils/uiSession'

// TWO QUESTIONS, TWO TOKENS. Round 8 answered both with `route_request` and got both wrong.
//
//   route_request   REQUEST OWNERSHIP — "is my response still the newest save of this kind?"
//                   Claimed by an ACTION when it starts. Gates the SNACKBAR: a superseded save
//                   must not overwrite a newer one's verdict.
//   dialog_session  UI SESSION — "is the dialog I would close still the one I was opened on?"
//                   Advanced by a UI TRANSITION (see `showDialog`). Gates the CLOSE.
//
// They are not interchangeable. Closing the dialog is a transition, so it advances the session —
// and NOT the request token, which is why the success message that follows the close still gets
// through. Opening it is also a transition, so `INIT_EDIT_ROUTE_DIALOG` ends the previous session
// exactly as an explicit close does; that is the whole of round 9's second finding.
const route_request = createLatestOnly()
const dialog_session = createUiSession()

// THE ONE TRANSITION PATH. Every mutation that changes which dialog is on screen goes through
// this, so a new open/close mutation cannot forget to end the previous session — which is how
// `INIT_EDIT_ROUTE_DIALOG` came to be the one open path that did not.
// `tests/wiring.test.mjs` fails if `state.visibility` is written anywhere else.
function showDialog(state, visible) {
  state.visibility = visible
  dialog_session.transition()
}

export default {
  state: {
    visibility: false,
    from_level: null,
    selected_route_data: null
  },
  mutations: {
    // The open path MenuSetting.vue actually uses.
    INIT_EDIT_ROUTE_DIALOG(state, payload) {
      state.from_level = payload.from_level
      state.selected_route_data = payload.data
      showDialog(state, true)
    },
    SET_EDIT_ROUTE_DIALOG_VISIBILITY(state, payload) {
      showDialog(state, payload)
    }
  },
  actions: {
    async updateRoute({ commit, rootState, dispatch }, payload) {
      // MUTATION, not a read. The server has already done this; being superseded cannot undo it,
      // so a SUCCESS must always reconcile local state. Only the DISPLAY — dialog visibility and
      // snackbar text — is ownership-gated. (Round 7 applied the read rule here and the UI kept
      // showing rows the server had deleted.) See utils/latestOnly.js.
      const isLatest = route_request()
      // Captured, not claimed: two saves can belong to the same open dialog at once, and both do.
      const sameDialog = dialog_session.current()
      const say = snackbar => {
        if (isLatest()) commit('SET_SNACKBAR', snackbar)
      }
      // Captured BEFORE the await. Moving the reconciliation out of the ownership gate means it
      // now runs for a superseded request too — and by then the operator may have opened the
      // dialog for a DIFFERENT route, so reading these afterwards would refresh the wrong level.
      // The request must reconcile the route IT changed.
      const from_level = rootState.editRouteDialog.from_level
      const parent_id = rootState.editRouteDialog.selected_route_data && rootState.editRouteDialog.selected_route_data.parent_id
      // Once the server has ANSWERED, a local failure is not a server failure. `commit` runs Vuex
      // subscribers and a throwing one propagates, so everything after the response — reconciling,
      // closing, even the snackbar itself — sits inside this try. Reporting "Server Error" there
      // tells the operator their change failed when the server accepted it, which is the most
      // damaging thing this action can say. The request's own failure still reports normally.
      let answered = false
      try {
        say({ status: true, color: 'loading', text: 'Updating route...', loading: true })
        const response = await api.updateRoute(payload)
        answered = true
        if (response.data.success) {
          // RECONCILE — but only what is ON SCREEN. Re-fetching is itself a repaint of the one
          // visible panel, so an older mutation whose parent is no longer displayed must NOT pull
          // the operator's panel back to it. If its change is not on screen, nothing on screen is
          // stale, and the next visit to that parent reads it fresh.
          if (from_level === 1) {
            dispatch('fetchRootLevelRoute')
          } else if (from_level === 2) {
            if (rootState.menuSetting.level_2_menu_data.route_id === parent_id) dispatch('fetchLevel2Route', parent_id)
          } else if (rootState.menuSetting.level_3_menu_data.route_id === parent_id) {
            dispatch('fetchLevel3Route', parent_id)
          }
          say({ status: true, color: 'success', text: 'Success', icon: 'check', timeout: '2000' })
          // BOTH tokens must hold to close, because closing makes two claims at once.
          //   isLatest()   my verdict is the current one; a newer save that FAILED must be allowed
          //                to keep the dialog open with the operator's unsaved edits in it.
          //   sameDialog() the dialog on screen is still the one I was opened on; after an
          //                INIT_* for another route it is not, and closing it would shut a dialog
          //                this save knows nothing about.
          // The close advances the SESSION only, never the request token — that is what stopped it
          // suppressing the message above.
          // DISPLAY — after the verdict, on purpose. `commit` runs Vuex subscribers and a throwing
          // one propagates out of it, so closing first meant a SUCCESSFUL save could be reported
          // as 'Server Error' from this action's own catch. The operator's verdict goes out first;
          // the close is the cosmetic half. (Suppression is no longer a reason for this order —
          // the close advances the SESSION, not the request token — but subscriber failure is.)
          if (isLatest() && sameDialog()) commit('SET_EDIT_ROUTE_DIALOG_VISIBILITY', false)
        } else {
          say({ status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '2000' })
        }
      } catch (error) {
        if (!answered) say({ status: true, color: 'error', text: 'Server Error', icon: 'close', timeout: '2000' })
      }
    },
    async deleteRoute({ commit, rootState, dispatch }, payload) {
      // MUTATION, not a read. The server has already done this; being superseded cannot undo it,
      // so a SUCCESS must always reconcile local state. Only the DISPLAY — dialog visibility and
      // snackbar text — is ownership-gated. (Round 7 applied the read rule here and the UI kept
      // showing rows the server had deleted.) See utils/latestOnly.js.
      const isLatest = route_request()
      // Captured, not claimed: two saves can belong to the same open dialog at once, and both do.
      const sameDialog = dialog_session.current()
      const say = snackbar => {
        if (isLatest()) commit('SET_SNACKBAR', snackbar)
      }
      // Captured BEFORE the await. Moving the reconciliation out of the ownership gate means it
      // now runs for a superseded request too — and by then the operator may have opened the
      // dialog for a DIFFERENT route, so reading these afterwards would refresh the wrong level.
      // The request must reconcile the route IT changed.
      const from_level = rootState.editRouteDialog.from_level
      const parent_id = rootState.editRouteDialog.selected_route_data && rootState.editRouteDialog.selected_route_data.parent_id
      // Once the server has ANSWERED, a local failure is not a server failure. `commit` runs Vuex
      // subscribers and a throwing one propagates, so everything after the response — reconciling,
      // closing, even the snackbar itself — sits inside this try. Reporting "Server Error" there
      // tells the operator their change failed when the server accepted it, which is the most
      // damaging thing this action can say. The request's own failure still reports normally.
      let answered = false
      try {
        say({ status: true, color: 'loading', text: 'Deleting route...', loading: true })
        const response = await api.deleteRoute(payload)
        answered = true
        if (response.data.success) {
          // RECONCILE — but only what is ON SCREEN. Re-fetching is itself a repaint of the one
          // visible panel, so an older mutation whose parent is no longer displayed must NOT pull
          // the operator's panel back to it. If its change is not on screen, nothing on screen is
          // stale, and the next visit to that parent reads it fresh.
          //
          // A level-1 route has no parent: it used to fall into the level-3 branch and request
          // `route_id=undefined`, leaving the deleted root on screen.
          if (from_level === 1) {
            dispatch('fetchRootLevelRoute')
          } else if (from_level === 2) {
            if (rootState.menuSetting.level_2_menu_data.route_id === parent_id) dispatch('fetchLevel2Route', parent_id)
            // The level-3 panel lists children of the route just deleted, so it must close — but
            // only if it is still showing THAT route's children.
            if (rootState.menuSetting.level_3_menu_data.route_id === payload.params.route_id) {
              commit('SET_MENU_SETTING_LEVEL_3_MENU_VISIBILITY', false)
            }
          } else if (rootState.menuSetting.level_3_menu_data.route_id === parent_id) {
            dispatch('fetchLevel3Route', parent_id)
          }
          say({ status: true, color: 'success', text: 'Success', icon: 'check', timeout: '2000' })
          // DISPLAY — gated on both tokens, and after the verdict; see updateRoute for both reasons.
          if (isLatest() && sameDialog()) commit('SET_EDIT_ROUTE_DIALOG_VISIBILITY', false)
        } else {
          say({ status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '2000' })
        }
      } catch (error) {
        if (!answered) say({ status: true, color: 'error', text: 'Server Error', icon: 'close', timeout: '2000' })
      }
    }
  }
}
